import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { QrCode, Menu, X, User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useAuth } from '../../context/AuthProvider';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session, signOut } = useAuth();

  const navigation = [
    { name: t('header.nav.home'), href: '/', current: location.pathname === '/' },
    { name: t('header.nav.generate'), href: '/generate', current: location.pathname === '/generate' },
    { name: t('header.nav.templates'), href: '/templates', current: location.pathname === '/templates' },
    { name: t('header.nav.dynamic'), href: '/dynamic', current: location.pathname.startsWith('/dynamic') },
    { name: t('header.nav.dashboard'), href: '/dashboard', current: location.pathname === '/dashboard' },
    { name: t('header.nav.account'), href: '/account', current: location.pathname === '/account' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleGetStarted = () => {
    navigate('/generate');
  };

  const handleSignIn = () => {
    navigate('/account');
  };

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/home" className="flex items-center space-x-2">
            <div className="relative">
              <QrCode className="h-8 w-8 gradient-text" />
              <div className="absolute inset-0 h-8 w-8 gradient-primary rounded opacity-20"></div>
            </div>
            <span className="text-xl font-bold gradient-text">
              {t('header.title')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'text-sm font-medium transition-colors hover:text-blue-600',
                    item.current
                      ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                      : 'text-gray-700'
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            
            {/* Language Selector in Main Nav */}
            <LanguageSelector />
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {session ? (
              <>
                <Link
                  to="/dashboard"
                  className="btn-outline"
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('header.nav.dashboard')}
                </Link>
                <button
                  onClick={handleSignOut}
                  className="btn-secondary"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('account.profile.signout')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSignIn}
                  className="btn-outline"
                >
                  <User className="h-4 w-4 mr-2" />
                  {t('account.signin.button')}
                </button>
                <button
                  onClick={handleGetStarted}
                  className="btn-gradient"
                >
                  {t('home.hero.cta')}
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-blue-600"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50 rounded-lg mt-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'block px-3 py-2 rounded-md text-base font-medium transition-colors',
                    item.current
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100'
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Language Selector - Prominent Position */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="px-3 py-2 mb-4">
                  <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    {t('header.language')}
                  </div>
                  <LanguageSelector />
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 space-y-2">
                {session ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="w-full flex items-center px-3 py-2 text-gray-700 hover:text-blue-600"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      {t('header.nav.dashboard')}
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center px-3 py-2 text-gray-700 hover:text-blue-600"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('account.profile.signout')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        handleSignIn();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center px-3 py-2 text-gray-700 hover:text-blue-600"
                    >
                      <User className="h-4 w-4 mr-2" />
                      {t('account.signin.button')}
                    </button>
                    <button
                      onClick={() => {
                        handleGetStarted();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center px-3 py-2 btn-gradient rounded-md"
                    >
                      {t('home.hero.cta')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
